import { useState, useRef, useEffect, useMemo, useCallback } from 'react';

import { searchFilterHandler } from './Select.helpers';

export const useSelectPresenter = ({
  name,
  value,
  list,
  disabled,
  onClick,
  onChange,
  onBlur,
  onSearch,
}) => {
  const selectorRef = useRef();
  const dropDownRef = useRef();
  const [isDropDownShown, setIsDropDownShown] = useState(false);
  const [searchStr, setSearchStr] = useState('');

  const filteredList = useMemo(() => {
    const filterFunc = onSearch || searchFilterHandler;
    return list.filter((item) => {
      return filterFunc(searchStr, item);
    });
  }, [list, searchStr, onSearch]);

  const selectedItem = useMemo(() => {
    return list.find((el) => el.id === value);
  }, [list, value]);

  const toggleDropDown = useCallback(() => {
    setIsDropDownShown((state) => !state);
  }, []);

  const showDropDown = useCallback(() => {
    setIsDropDownShown(true);
  }, []);

  const hideDropDown = useCallback(() => {
    setSearchStr('');
    setIsDropDownShown(false);
  }, []);

  const outsideClickHandler = useCallback(
    (e) => {
      if (e?.target && dropDownRef.current && dropDownRef.current.contains(e.target)) {
        return;
      }

      onBlur();
      hideDropDown();
    },
    [dropDownRef]
  );

  const itemClickHandler = useCallback(
    (item) => {
      onChange(item, name);
      hideDropDown();
    },
    [hideDropDown, name]
  );

  const selectorChangeHandler = useCallback(
    (e) => {
      const item = list.find((i) => i.title === e.target?.value);
      itemClickHandler(item);
    },
    [list, itemClickHandler]
  );

  const selectorMouseDownHandler = useCallback(
    (e) => {
      e.preventDefault();
      if (disabled) {
        return;
      }
    },
    [disabled]
  );

  const selectorClickHandler = useCallback(
    (e, name) => {
      e.preventDefault();
      if (disabled) {
        return;
      }
      onClick(e, name);
      toggleDropDown();
    },
    [onClick, toggleDropDown, disabled]
  );

  const searchStrChangeHandler = useCallback((e) => {
    const val = e.target.value;
    setSearchStr(val);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', hideDropDown);
    window.addEventListener('resize', hideDropDown);
    return () => {
      window.removeEventListener('scroll', hideDropDown);
      window.removeEventListener('resize', hideDropDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    searchStr,
    isDropDownShown,
    selectorRef,
    dropDownRef,
    filteredList,
    selectedItem,
    toggleDropDown,
    showDropDown,
    hideDropDown,
    outsideClickHandler,
    itemClickHandler,
    selectorChangeHandler,
    selectorMouseDownHandler,
    selectorClickHandler,
    searchStrChangeHandler,
  };
};
